/**
 * Projects panel — file management for the local IndexedDB workspace.
 * Rename the open project, create/duplicate/delete projects, switch between
 * them (thumbnail cards, newest first), and round-trip self-contained
 * .novacad files for backup and sharing.
 */

import type { App } from '../app';
import type { ProjectRecord } from '@core/storage/db';

export function mountProjectsPanel(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-side';
  panel.style.display = 'none';
  host.appendChild(panel);

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = '.novacad,application/json';
  fileInput.style.display = 'none';
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (f) void app.openProjectFile(f);
    fileInput.value = '';
  });
  panel.appendChild(fileInput);

  let projects: ProjectRecord[] = [];
  let wasOpen = false;

  const refresh = async (): Promise<void> => {
    // Snapshot the current view onto the open project's card, then list.
    await app.captureThumbnail().catch(() => undefined);
    projects = await app.listProjects();
    render(false);
  };

  const render = (checkOpen = true): void => {
    const open = app.activePanel === 'projects';
    panel.style.display = open ? 'block' : 'none';
    if (!open) {
      wasOpen = false;
      return;
    }
    if (checkOpen && !wasOpen) {
      wasOpen = true;
      void refresh(); // re-renders when the list arrives
      return;
    }
    panel.innerHTML = '<h3>Projects</h3>';
    panel.appendChild(fileInput);

    // Current project name (rename on change).
    const nameWrap = document.createElement('div');
    nameWrap.className = 'field';
    nameWrap.innerHTML = '<label>Current project</label>';
    const name = document.createElement('input');
    name.type = 'text';
    name.value = app.doc.name;
    name.addEventListener('change', () => void app.renameProject(name.value).then(refresh));
    nameWrap.appendChild(name);
    panel.appendChild(nameWrap);

    // Actions.
    const actions = document.createElement('div');
    actions.className = 'proj-actions';
    for (const [label, fn] of [
      ['New', () => app.newProject().then(refresh)],
      ['Duplicate', () => app.duplicateProject().then(refresh)],
      ['Save file', () => app.downloadProject()],
      ['Open file…', async () => fileInput.click()],
    ] as const) {
      const b = document.createElement('button');
      b.textContent = label;
      b.addEventListener('click', () => void fn());
      actions.appendChild(b);
    }
    panel.appendChild(actions);

    // Project cards.
    for (const p of projects) {
      const card = document.createElement('div');
      card.className = 'proj-card' + (p.id === app.doc.id ? ' current' : '');
      const thumb = document.createElement('div');
      thumb.className = 'proj-thumb';
      if (p.thumbnail) {
        const img = document.createElement('img');
        img.src = p.thumbnail;
        thumb.appendChild(img);
      }
      const meta = document.createElement('div');
      meta.className = 'proj-meta';
      const when = new Date(p.updatedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      meta.innerHTML =
        `<span class="proj-name">${p.name}</span>` +
        `<span class="proj-date">${p.timeline.features.length} features · ${when}</span>`;
      const del = document.createElement('button');
      del.className = 'proj-del';
      del.title = 'Delete project';
      del.textContent = '✕';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        void app.deleteProject(p.id).then(refresh);
      });
      card.append(thumb, meta);
      // Assemblies: insert another project as a linked component.
      if (p.id !== app.doc.id) {
        const ins = document.createElement('button');
        ins.className = 'proj-del proj-insert';
        ins.title = `Insert ${p.name} into ${app.doc.name} as an assembly component`;
        ins.textContent = '⊞';
        ins.addEventListener('click', (e) => {
          e.stopPropagation();
          void app.insertPart(p.id);
        });
        card.appendChild(ins);
      }
      card.appendChild(del);
      card.addEventListener('click', () => {
        if (p.id !== app.doc.id) void app.switchProject(p.id).then(refresh);
      });
      panel.appendChild(card);
    }
  };

  app.onUI(() => render());
  render();
}
